import type { Messages } from "./types";

export const en: Messages = {
  brand: { name: "Tenqora Agent", domain: "agent.tenqora.net" },
  lang: { switchLabel: "Language", en: "English", de: "Deutsch" },
  common: {
    skipToContent: "Skip to content",
    back: "Back",
    loading: "Loading…",
    retry: "Retry",
    cancel: "Cancel",
    save: "Save",
    create: "Create",
    delete: "Delete",
    confirm: "Confirm",
    signingIn: "Signing in…",
    saving: "Saving…",
    creating: "Creating…",
    sending: "Sending…",
    unknown: "Unknown",
    noDescription: "No description",
    showPassword: "Show password",
    hidePassword: "Hide password",
    showPasswordAction: "Show",
    hidePasswordAction: "Hide",
    onlineRequired: "Reconnect to make changes.",
    pilotBanner: "Pilot · Demo rental is labelled. Live rental is not available yet.",
    operatorBanner:
      "Operator workspace · Fleet inventory and allowlisted diagnostics. Not shown to tenants.",
    toSite: "Website",
    privacy: "Privacy",
    terms: "Pilot terms",
    signIn: "Sign in",
    signOut: "Sign out",
    register: "Register",
    open: "Open",
  },
  nav: {
    overview: "Overview",
    environment: "Environment",
    machines: "Macs",
    projects: "Projects",
    tasks: "Tasks",
    connections: "Connections",
    account: "Account",
    operator: "Operator",
    admin: "Administration",
    fleet: "Fleet",
    cabinet: "Cabinet",
    workspaceRequests: "Workspace requests",
    primary: "Primary navigation",
    sections: "Sections",
  },
  titles: {
    home: "Tenqora Agent",
    login: "Sign in · Tenqora Agent",
    register: "Register · Tenqora Agent",
    privacy: "Privacy · Tenqora Agent",
    terms: "Pilot terms · Tenqora Agent",
    overview: "Overview · Tenqora Agent",
    environment: "Environment · Tenqora Agent",
    environmentSave: "Save and restore preview · Tenqora Agent",
    machines: "Macs · Tenqora Agent",
    projects: "Projects · Tenqora Agent",
    project: "Project · Tenqora Agent",
    workspaceRequests: "Workspace request · Tenqora Agent",
    tasks: "Tasks · Tenqora Agent",
    task: "Task · Tenqora Agent",
    lease: "Lease · Tenqora Agent",
    operation: "Operation · Tenqora Agent",
    connections: "Connections · Tenqora Agent",
    account: "Account · Tenqora Agent",
    operator: "Operator · Tenqora Agent",
    admin: "Administration · Tenqora Agent",
    operatorWorkspaceRequests: "Workspace requests · Operator",
    notFound: "Page not found · Tenqora Agent",
  },
  landing: {
    headline: "Your Mac.\nYour agents.\nOne workspace.",
    lead: "A focused home for your projects, tasks and connected tools.",
    ctaStart: "Start workspace",
    ctaExplore: "Explore workspace",
    navSections: "Page sections",
    navWorkflow: "Workflow",
    navPreview: "Preview",
    navCapacity: "Capacity",
    pilotNote: "Accounts and projects are available; Mac rental is demo only.",
    artLabel: "Product photography",
    artAlt: "Silver compact Mac on an ivory plinth",
    workflowTitle: "Work, organized.",
    workflowLead: "Start a project. Connect your tools. Keep every step in view.",
    workflow1Title: "Organize",
    workflow1Body: "Keep projects and planned tasks together for your organization.",
    workflow2Title: "Connect",
    workflow2Body: "Store provider connections securely when you are ready to use them.",
    workflow3Title: "Follow progress",
    workflow3Body: "Watch accepted work move through status until it finishes or stops.",
    previewTitle: "Everything in view.",
    previewLead: "Projects, storage and account settings, together in one workspace.",
    previewBadge: "Preview",
    previewLane1Title: "Environment",
    previewLane1Body: "Your activity and capacity requests in one place.",
    previewLane2Title: "Projects & tasks",
    previewLane2Body: "Structured work with clear states and results when available.",
    previewLane3Title: "Account",
    previewLane3Body: "Profile, sessions, and language — controlled by you.",
    capacityTitle: "Storage, archive, backups",
    capacityLead: "Request future capacity for your organization. Allocation follows review.",
    capacityStorageTitle: "Project storage",
    capacityStorageBody:
      "Working space you can request for project materials. Allocation follows review — not automatic.",
    capacityArchiveTitle: "Archive",
    capacityArchiveBody:
      "Longer-retention capacity you can request. Save, restore, and verified workspace archives are not available in this pilot.",
    capacityBackupTitle: "Backups",
    capacityBackupBody:
      "Protective copy capacity you can request for your Mac workspace. Scheduled workspace backups are not available in this pilot.",
    capacityAvailability: "Request only · payment not connected",
    bottomTitle: "Open your workspace",
    bottomLead: "Create an organization account and begin with projects and tasks.",
    footerCopy: "© Tenqora Agent",
  },
  auth: {
    loginTitle: "Sign in",
    loginLead: "Enter your organization workspace.",
    registerTitle: "Create an account",
    registerLead: "Start a workspace for your organization.",
    email: "Email",
    password: "Password",
    name: "Name",
    organization: "Organization",
    emailRequired: "Enter your email",
    passwordRequired: "Enter your password",
    nameRequired: "Enter your name",
    orgRequired: "Enter an organization name",
    passwordMin: "Use at least 12 characters",
    ackRequired: "Confirm the pilot terms to continue",
    ackLabel:
      "I understand this is a Tenqora Agent pilot: account and project data are saved; Mac rental is demo only; I have read Privacy and Pilot terms.",
    loginAction: "Sign in",
    registerAction: "Create account",
    noAccount: "No account?",
    hasAccount: "Already registered?",
    loginFailed: "Could not sign in",
    registerFailed: "Could not create the account",
  },
  legal: {
    privacyTitle: "Privacy (pilot)",
    privacyP1:
      "In pilot mode, Tenqora Agent stores account, organization, project, and task data needed for the cabinet. Passwords are stored as irreversible hashes. Sessions use a protected HttpOnly cookie.",
    privacyP2:
      "The frontend does not write passwords, CSRF tokens, or connection secrets to localStorage. Integration secrets are encrypted on the server and never returned by the API. Demo Mac rental does not issue SSH credentials or create a machine account, and is not access to the physical management Mac.",
    privacyP3:
      "Public pilot address: https://agent.tenqora.net. This text describes pilot behavior and is not a claim of a specific law, certificate, or retention schedule — retention periods are not finalized.",
    termsTitle: "Pilot terms",
    termsP1:
      "Tenqora Agent is provided as a pilot. The interface and API may change. There are no tariffs, payments, or commercial SLA guarantees.",
    termsP2:
      "Public rental of the current management Mac is unavailable. Labelled demo rental without SSH or a machine account is available. Live telemetry and allowlisted diagnostics are operator-only.",
    termsP3:
      "Target access model: each renter gets a separate standard macOS account for the lease; the operator account is never shared. That is a policy requirement, not implemented MDM. Auto-provisioning and MDM are future stages after prerequisites are verified.",
    termsP4:
      "Plan tasks produce a deterministic checklist and are not LLM generation. Stored provider keys are not wired to AI task runs. Users do not receive arbitrary shell, PDU, MCP servers, or infrastructure administration.",
    termsP5: "No seller legal entity or commercial offer is declared in this pilot.",
  },
  notFound: {
    code: "404",
    title: "Page not found",
    body: "This address does not exist, or the resource is not available in your context. We do not confirm whether other tenants’ objects exist.",
    home: "Back to home",
  },
  states: {
    sessionEnded: "Session ended",
    sessionEndedBody: "Sign in again to continue.",
    forbidden: "Not allowed",
    forbiddenBody: "You do not have permission for this action.",
    offline: "You’re offline",
    offlineBody: "Changes are disabled until the connection returns. Secrets are never cached.",
    error: "Something went wrong",
    empty: "Nothing here yet",
    loadFailed: "Could not load",
  },
  api: {
    invalidJson: "The server returned an unexpected response. Try again later.",
    sessionExpired: "Session ended. Sign in again.",
    forbidden: "You do not have permission for this action.",
    offline: "You’re offline. Changes are unavailable.",
    network: "Network unavailable. Try again later.",
    httpError: "Request failed ({status}).",
  },
  dashboard: {
    title: "Overview",
    lead: "Your projects, tasks, and active demo leases.",
    projects: "Projects",
    tasks: "Tasks",
    activeLeases: "Active demo leases",
    recentTasks: "Recent tasks",
    recentStates: "States in the loaded tasks",
    recentStatesNote: "Only the list below. Not a full history.",
    noTasks: "No tasks yet",
    noTasksBody: "Open a project to create a planned task and follow its progress.",
    noProjects: "No projects yet",
    noProjectsBody: "Create a project to organize work for your organization.",
    openProjects: "Go to projects",
  },
  machines: {
    title: "Demo rental",
    lead: "Labelled demo rental for your organization. Live rental is not available in this pilot.",
    empty: "No demo machines",
    emptyBody: "No demo rental options are available in your organization right now.",
    demoName: "Demo Mac studio",
    requestDemo: "Request demo lease",
    endLease: "End lease",
    leaseCard: "Lease details",
    requestTitle: "Request demo lease",
    requestConfirm: "Request",
    requestBody:
      "Acceptance is not a ready Mac. No SSH and no machine account are issued. A conflicting reservation returns an error.",
    endTitle: "End lease",
    endConfirm: "End lease",
    endBody: "Access revoke is requested asynchronously. Disconnect is confirmed only after the server finishes.",
    endAck: "I understand disconnect is confirmed only after the server finishes.",
    modelUnknown: "model unknown",
    leasePrefix: "Lease",
    mode: "mode",
    lastSeen: "last seen",
  },
  leases: {
    title: "Lease",
    back: "Machines",
    noSsh: "No SSH · demo does not create a Mac account",
    endingTitle: "Ending in progress",
    endingBody: "Access revoke is not confirmed yet. Disconnect is never instantaneous.",
    endAction: "End lease",
    endTitle: "End demo lease",
    endBody: "This runs asynchronously. Access is revoked only after the server confirms completion.",
    endAck: "I understand confirmation comes from the server only.",
    notFound: "Lease not found",
  },
  projects: {
    title: "Projects",
    lead: "Organize your work and keep the next step clear.",
    newTitle: "New project",
    name: "Name",
    description: "Description",
    nameRequired: "Enter a project name",
    createFailed: "Could not create the project",
    empty: "No projects",
    emptyBody: "Create your first project above.",
    detailLead: "Create a plan task for a structured checklist. Diagnostics require an operator role.",
    newTask: "New task",
    taskTitle: "Title",
    instructions: "Instructions",
    kind: "Kind",
    kindPlan: "Plan checklist",
    kindDiagnostics: "Diagnostics (operator)",
    titleRequired: "Enter a title",
    instructionsRequired: "Enter instructions",
    diagnosticsOnlyOperator: "Diagnostics are available to operators only",
    createTask: "Create task",
    createTaskFailed: "Could not create the task",
    connectionsTitle: "Project connections",
    connectionsLead: "Attach your organization’s saved connections to this project. Detach removes the binding only.",
    connectionsEmpty: "No connections attached",
    connectionsEmptyBody: "Attach a saved connection below, or add one under Connections.",
    connectionsFailed: "Could not load project connections",
    retryConnections: "Try again",
    availableFailed: "Could not load available connections",
    retryAvailable: "Try again",
    capChecklist: "Built-in non-AI checklist",
    capAi: "AI execution",
    capRepo: "Repository execution",
    capAvailable: "available",
    capUnavailable: "unavailable",
    verificationNote:
      "Verification is a point-in-time read-only credential check. It does not prove provider permissions, scopes, or budget.",
    attachTitle: "Attach a connection",
    attachSelect: "Saved connection",
    attachPlaceholder: "Select a connection",
    attachNone: "No connections left to attach",
    attachNeedConnections: "Save a connection first under Connections.",
    attachAllBound: "All saved connections are already attached.",
    attach: "Attach",
    attaching: "Attaching…",
    attachFailed: "Could not attach the connection",
    manageConnections: "Manage connections",
    detach: "Detach",
    detachTitle: "Detach connection",
    detachBody:
      "Removes this connection from the project only. The encrypted key stays in Connections and is not revoked at the provider.",
    detachConfirm: "Detach",
    detachFailed: "Could not detach the connection",
    setupTitle: "Project setup",
    setupLead:
      "For interactive Mac work, iOS builds, or agents: prepare credentials here first. Live Mac provisioning and agent execution are not available in this pilot.",
    setupConnections: "Project connections attached",
    setupConnectionsReady: "{count} saved connection(s) attached to this project.",
    setupConnectionsNeeded: "Attach a saved connection below, or add one under Connections.",
    setupVerification: "Verification freshness",
    setupVerificationNone: "No attached connections to check yet.",
    setupVerificationSummary:
      "verified {verified} · stale {stale} · failed {failed} · not checked {not_checked}. Point-in-time only — not provider scopes or budget.",
    setupChecklistTask: "Plan checklist tasks",
    setupChecklistReady: "Available now: create a deterministic plan checklist task below.",
    setupChecklistUnavailable: "Checklist tasks are not reported as available for this project.",
    setupAi: "AI / agent execution",
    setupAiUnavailable: "Unavailable. Attached keys do not enable agent runs on a Mac.",
    setupRepo: "Repository execution",
    setupRepoUnavailable: "Unavailable. No CI runner or repository job execution in this pilot.",
    setupMac: "Live Mac provisioning",
    setupMacUnavailable:
      "Unavailable. No automated tenant Mac allocation, remote desktop gateway, or SSH account issuance.",
    setupDemoHint: "You can explore labelled demo rental only — no SSH and no machine account.",
    setupDemoLink: "Open demo rental",
    setupCreateTask: "Create checklist task",
    setupNextSteps:
      "Next: attach and verify connections, submit a workspace request if you need a Mac later, create a plan checklist task, and use demo rental only as a labelled preview. No live provisioning yet.",
    workspaceRequestLink: "Request a workspace",
    activityTitle: "Project activity",
    activityLead:
      "Task and Mac-request counts for this project from the task database. Not HTTP traffic, uptime, or live Mac telemetry.",
    activityWindow: "Period",
    activityWindow24h: "Last 24 hours (UTC)",
    activityWindow7d: "Last 7 days (UTC)",
    activityObserved: "Observed {when}",
    activitySource: "Source: task database",
    activityFailed: "Could not load project activity",
    activityRetry: "Try again",
    activityRefresh: "Refresh",
    activityRefreshing: "Refreshing…",
    activityTableToggle: "Show activity table (UTC buckets)",
    activityScaleMax: "Chart scale max {max}",
    activityUtcNote: "Bucket times are UTC",
    activityTaskCounts: "Current task states",
    activityTotal: "Total tasks",
    activityMacRequests: "Mac workspace requests",
    activitySeriesTitle: "Task activity over time",
    activitySeriesLead:
      "Created counts new tasks. Succeeded and failed count persisted state transitions. The last bucket may be partial.",
    activityCreated: "Created",
    activitySucceeded: "Succeeded",
    activityFailedEvents: "Failed",
    activityChartSummary:
      "{window}: {created} created, {succeeded} succeeded, {failed} failed across the period.",
    activityTableCaption: "Task activity by time bucket",
    activityBucket: "Bucket (UTC)",
    activityCapsNote:
      "Application metrics and archive/restore are not available for this project. Absent capabilities are not shown as zero.",
    activityRecent: "Recent tasks",
    activityRecentEmpty: "No recent tasks",
    servicesTitle: "Applications",
    servicesLead:
      "Monitored applications bound to this project. Probe duration is one synthetic HTTP check — not customer request traffic or latency percentiles.",
    servicesSource: "Source: kubernetes probe",
    servicesSourceProbe: "kubernetes probe",
    servicesObserved: "List observed {when}",
    servicesRefresh: "Refresh",
    servicesRefreshing: "Refreshing…",
    servicesRetry: "Try again",
    servicesFailed: "Could not load applications",
    servicesEmpty: "No monitored applications",
    servicesEmptyBody:
      "No application is connected for monitoring on this project yet. This is not a provisioning or deployment control.",
    servicesCapsNote:
      "Deployment controls and HTTP traffic metrics are unavailable. Missing signals stay unknown — they are not shown as zero.",
    servicesUnknown: "unknown",
    servicesLastObserved: "Last sample",
    servicesReplicas: "Replicas ready / desired",
    servicesReplicasValue: "{ready} / {desired}",
    servicesProbeResult: "Probe result",
    servicesProbeOk: "success",
    servicesProbeFail: "failed",
    servicesProbeDuration: "Probe duration",
    servicesProbeNote:
      "Duration is from a synthetic collector probe only. It is not real user latency or request volume.",
    servicesCollectionOk: "collected",
    servicesCollectionUnavailable: "collector unavailable",
    servicesNoSamples: "No samples yet for this application.",
    servicesChartTitle: "Probe duration samples",
    servicesScaleMax: "Chart scale max {max} ms",
    servicesChartSummary: "{count} samples · scale max {max} ms",
    servicesChartRange: "Samples {from} – {to} UTC",
    servicesTableToggle: "Show sample table (UTC)",
    servicesTableCaption: "Application probe samples",
    servicesColWhen: "Observed (UTC)",
    servicesColCollection: "Collection",
    servicesReleasesTitle: "Release observations",
    servicesReleasesLead:
      "Observed Kubernetes rollout states for this application. Not deploy commands and not evidence of user request traffic.",
    servicesReleasesEmpty: "No release observations yet.",
    servicesReleaseRevision: "Revision {revision}",
    servicesReleaseProgressing: "progressing",
    servicesReleaseAvailable: "available",
    servicesReleaseFailed: "failed",
    alertsTitle: "Notifications",
    alertsLead:
      "In-app incidents from observed application health for this project. Evaluation runs only when you enable it here.",
    alertsChannelNote:
      "Delivery is in-app only. Email and push notifications are not configured.",
    alertsChannelReported:
      "The session reports email delivery. This page does not send email or push.",
    alertsObserved: "List observed {when}",
    alertsRefresh: "Refresh",
    alertsRefreshing: "Refreshing…",
    alertsRetry: "Try again",
    alertsFailed: "Could not load notifications",
    alertsEmpty: "No incidents",
    alertsEmptyBody: "No open or recent incidents for monitored applications on this project.",
    alertsDisabledNote:
      "In-app monitoring is off. New incidents are not evaluated. Existing history stays listed and is not marked resolved.",
    alertsEnabledLabel: "In-app monitoring on",
    alertsDisabledLabel: "In-app monitoring off",
    alertsEnable: "Enable in-app monitoring",
    alertsDisable: "Disable in-app monitoring",
    alertsPrefFailed: "Could not update notification preference",
    alertsReason: "Reason",
    alertsReasonDegraded: "degraded",
    alertsReasonUnavailable: "unavailable",
    alertsReasonStale: "stale",
    alertsStatusOpen: "open",
    alertsStatusResolved: "resolved",
    alertsOpened: "Opened {when}",
    alertsResolved: "Resolved {when}",
    alertsAcknowledged: "Acknowledged {when}",
    alertsAcknowledge: "Acknowledge",
    alertsAcknowledging: "Acknowledging…",
    alertsAckFailed: "Could not acknowledge incident",
    alertsAckNote: "Acknowledgment records that you saw the incident. It does not mean the application recovered.",
  },
  workspace: {
    title: "Workspace request",
    lead: "Tell us what Mac workspace you need for this project. An operator will review your request. There is no charge yet, and no machine is allocated yet.",
    bounds:
      "These are requested requirements for review. They may not be available, and submitting does not reserve or start a workspace.",
    newTitle: "New request",
    profile: "Intended use",
    profileRequired: "Choose an intended-use profile",
    toolsLabel: "Requested tools",
    memory: "Requested memory",
    memoryHint: "How much memory you are asking for — subject to review.",
    memoryOption: "{gb} GB requested",
    memoryRequired: "Choose a requested memory option",
    storage: "Requested storage (GB)",
    storageHint: "How much storage you are asking for — subject to review.",
    storageRange: "Enter storage between 128 and 4096 GB",
    duration: "Requested duration (days)",
    durationHint: "How long you are asking to keep the workspace — subject to review.",
    durationRange: "Enter duration between 1 and 30 days",
    notes: "Notes",
    notesHint: "Optional context for review. Do not include passwords or API keys.",
    notesMax: "Notes must be at most 2000 characters",
    submit: "Submit request",
    submitFailed: "Could not submit the workspace request",
    conflictActive:
      "This project already has an active workspace request, or the request changed. Reloaded the list — review it before trying again.",
    listTitle: "Request history",
    empty: "No workspace requests yet",
    emptyBody: "Submit a request above when you are ready. It will be reviewed; nothing is allocated from this form.",
    loadFailed: "Could not load workspace requests",
    retry: "Try again",
    summary: "{memory} GB RAM · {storage} GB storage · {days} days",
    reviewNoteLabel: "Operator note (visible to you)",
    cancel: "Cancel request",
    cancelTitle: "Cancel workspace request",
    cancelBody: "Cancels this request only. Your other project data stays as it is.",
    cancelConfirm: "Cancel request",
    cancelFailed: "Could not cancel the request",
    cancelConflict: "This request changed. Reloaded the list — try again if it is still active.",
    pageStatus: "Page {page} of {pages} · {total} requests",
    prevPage: "Previous",
    nextPage: "Next",
    planShow: "Show plan",
    planHide: "Hide plan",
    planTitle: "Request plan",
    planMode:
      "Planning only. This view shows the server plan for the request — it does not start work.",
    planNoExecution:
      "No execute control here. The plan is not executable: nothing is reserved, provisioned or run on a Mac from this screen.",
    planLoadFailed: "Could not load the request plan",
    planRetry: "Try again",
    planSourceLabel: "Plan source",
    planSourcePersisted: "Persisted snapshot saved with the request",
    planSourcePreview: "Legacy preview — no persisted snapshot for this request",
    planSnapshotAt: "saved {when}",
    planRequestState: "Request state",
    planPrerequisites: "Prerequisites (not met)",
    planPrerequisitesNone: "No prerequisite blockers listed.",
    planSteps: "Planned steps",
    planStepsNone: "No steps listed.",
    planCapabilities: "Capabilities",
    planCapProvisioning: "Provisioning",
    planCapAiExecution: "AI execution",
    planCapArchiveRestore: "Archive restore",
    planCapDisabled: "disabled",
    planExecutableLabel: "Executable",
    planExecutableNo: "no",
    planModeLabel: "Mode",
  },
  tasks: {
    title: "Tasks",
    lead: "Follow your work from the queue to the result.",
    empty: "No tasks yet",
    emptyBody: "Create a task from a project to start tracking work.",
    noResults: "No matching tasks",
    noResultsBody: "Try a different search or clear filters.",
    searchLabel: "Search",
    searchPlaceholder: "Search by title",
    stateLabel: "Status",
    stateAll: "All statuses",
    projectLabel: "Project",
    projectAll: "All projects",
    applyFilters: "Apply",
    refresh: "Refresh",
    refreshing: "Refreshing…",
    refreshStale: "Could not refresh",
    refreshFailed: "Could not refresh tasks",
    retryRefresh: "Try again",
    loadFailed: "Could not load tasks",
    projectsFailed: "Could not load projects",
    backToFirstPage: "Back to first page",
    createViaProjects: "Create task in a project",
    openProject: "Open project",
    pageStatus: "Page {page} of {pages} · {total} tasks",
    prevPage: "Previous",
    nextPage: "Next",
    back: "Tasks",
    instructions: "Instructions",
    result: "Result",
    noResult: "No result yet — wait until the operation finishes.",
    timelineTitle: "Progress",
    timelineScope: "History since tracking was enabled. Older tasks may have no earlier steps.",
    timelineEmpty: "No progress events yet for this task.",
    cancel: "Cancel task",
    cancelling: "Cancelling…",
    cancelRequested: "Cancel requested",
    cancelFailed: "Could not cancel the task",
    notFound: "Task not found",
  },
  checklist: {
    heading: "Structured checklist",
    engineNote: "Built-in checklist · not AI-generated",
    summary: "A structured project checklist. No external AI model was used.",
    stepLabel: "Step {n}",
    diagnosticsHeading: "Diagnostics snapshot",
    diagnosticsNote: "Allowlisted read-only observation from the operator Mac.",
    fieldCpu: "CPU",
    fieldMem: "Memory",
    fieldDisk: "Disk",
    fieldUptime: "Uptime",
    otherFields: "Additional fields",
  },
  checklistSteps: [
    {
      title: "Define the outcome",
      detail: "Capture the goal in your own words.",
    },
    {
      title: "Prepare a separate working branch",
      detail: "Check repository access and keep secrets out of source files.",
    },
    {
      title: "Implement and verify",
      detail: "Split the work into small changes, then run tests and a build.",
    },
    {
      title: "Review and release",
      detail: "Review the result before deploying to a separate environment.",
    },
  ],
  operations: {
    title: "Operation",
    lead: "Your request is being processed. This page updates as it progresses.",
    back: "Overview",
    runningTitle: "In progress…",
    runningBody: "Updating status until the operation finishes. Success appears only after server confirmation.",
    failedTitle: "Operation failed",
    canceledTitle: "Operation canceled",
    failedBody: "The server returned an unsuccessful result.",
    succeededTitle: "Operation confirmed",
    succeededBody: "The server recorded successful completion.",
    pollError: "Status update failed",
    openLease: "Open lease",
    openTask: "Open task",
    noResource: "Item not assigned yet",
    unknownResource: "Item cannot be opened yet",
    toMachines: "Back to machines",
    objectLabel: "item",
    waitingObject: "not assigned yet",
    opLabel: "operation",
  },
  account: {
    title: "Account",
    organization: "Organization",
    capabilities: "capabilities",
    profileTitle: "Profile",
    profileLead: "Update your display name. Organization rename is available to owners and admins only. Email and role are not editable here.",
    name: "Name",
    orgRenameHint: "Owners and admins can rename the organization.",
    orgRenameForbidden: "Only owners and admins can rename the organization.",
    nameRequired: "Enter a name (2–100 characters)",
    orgRequired: "Enter an organization name (2–100 characters)",
    profileSaved: "Profile saved.",
    profileFailed: "Could not save the profile",
    saveProfile: "Save profile",
    emailReadonly: "Email (read-only)",
    roleReadonly: "Role (read-only)",
    changePassword: "Change password",
    currentPassword: "Current password",
    newPassword: "New password",
    currentRequired: "Enter your current password",
    newMin: "New password must be at least 12 characters",
    changed: "Password changed. Other sessions were invalidated.",
    changeFailed: "Could not change the password",
    sessionsTitle: "Sessions",
    sessionsLead: "Active logins for your account. You can revoke other devices; this session stays signed in unless you sign out.",
    sessionCurrent: "This device",
    sessionExpires: "Expires",
    sessionCreated: "Created",
    revokeSession: "Revoke",
    revokeOthers: "Revoke other sessions",
    revokeOthersTitle: "Revoke other sessions?",
    revokeOthersBody: "All other logins will be signed out. This device stays signed in.",
    revokeOthersConfirm: "Revoke others",
    revokeConfirmTitle: "Revoke this session?",
    revokeConfirmBody: "That device will need to sign in again.",
    sessionsEmpty: "No sessions listed.",
    sessionsFailed: "Could not load sessions",
    revokeFailed: "Could not revoke the session",
    revokedCount: "Revoked {n} session(s).",
    audit: "Organization audit",
    auditEmpty: "No entries yet.",
    auditUnavailable: "Audit unavailable",
    auditUnknown: "Account activity",
    auditActions: {
      "account.login": "Signed in",
      "account.profile_updated": "Profile updated",
      "account.password_changed": "Password changed",
      "account.session_revoked": "Session revoked",
      "account.other_sessions_revoked": "Other sessions revoked",
      "account.registered": "Account created",
      "project.created": "Project created",
      "task.created": "Task created",
      "task.cancel_requested": "Task cancel requested",
      "demo.lease_requested": "Demo rental requested",
      "demo.lease_end_requested": "Demo rental end requested",
      "storage.requested": "Capacity request submitted",
      "storage.request_canceled": "Capacity request canceled",
      "integration.created": "Connection saved",
      "integration.deleted": "Connection deleted",
      "integration.verified": "Connection checked",
      "machine.maintenance_enabled": "Maintenance enabled",
      "machine.maintenance_disabled": "Maintenance cleared",
    },
    roles: {
      owner: "Owner",
      admin: "Admin",
      developer: "Developer",
      viewer: "Viewer",
      operator: "Operator",
    },
    roleUnknown: "Member",
    connections: "Connections",
    connectionsLead: "On narrow screens, open Connections from here. Keys are encrypted; stored is not verified.",
    openConnections: "Open connections",
    openEnvironment: "Open environment",
    pilotMeta: "Pilot mode · agent.tenqora.net",
  },
  environment: {
    title: "Your environment",
    lead: "Your projects, tasks, and capacity requests in one place",
    activityTitle: "Your activity",
    projects: "Projects",
    queued: "Queued tasks",
    running: "Running tasks",
    completed: "Completed tasks",
    connections: "Connections",
    demoLeases: "Demo leases",
    demoLeasesHint: "Labelled DEMO rental preview only.",
    resourcesTitle: "Live storage & archives",
    resourcesEmpty:
      "Nothing connected yet. Submitting a capacity request does not allocate storage, archive, or backup by itself.",
    resourcesEmptyProvisioning:
      "Storage provisioning is reported on, and no resource records were returned.",
    resourceIntro:
      "{n} record(s) returned. A returned record is not a verified archive and not an allocation.",
    resourceUndescribed: "This record has no fields the cabinet is allowed to show.",
    storageProvisioningOn:
      "Storage provisioning is reported on. Save, restore, export, and delete are still not connected here.",
    liveComputeOff: "Live compute is not connected. The counts above are records in this account.",
    archiveRestoreNote:
      "Workspace save, restore, and verified archives are not available in this pilot.",
    catalogTitle: "Capacity requests",
    catalogLead: "A request is kept for review. It does not allocate storage.",
    paymentNotConnected: "Payment not connected",
    productStorage: "Project storage",
    productArchive: "Archive",
    productBackup: "Backup",
    unitGb: "GB",
    availabilityRequest: "Request only",
    requestTitle: "New capacity request",
    product: "Capacity type",
    amount: "Amount (GB)",
    amountHint: "Whole number from 1 to 100000",
    amountRequired: "Enter an amount in GB",
    amountRange: "Enter a whole number from 1 to 100000",
    notes: "Notes (optional)",
    notesHint: "Up to 2000 characters",
    submit: "Submit request",
    submitting: "Submitting…",
    submitFailed: "Could not submit the request",
    conflict: "This request conflicts with a previous submission. Change the details or wait.",
    historyTitle: "Your requests",
    requestStates: "States in the loaded requests",
    requestStatesNote: "Only the list below. A request is not storage, a save, or a payment.",
    historyEmpty: "No capacity requests yet.",
    cancel: "Cancel request",
    cancelTitle: "Cancel this request?",
    cancelBody: "The request will be marked canceled. Nothing was purchased or allocated.",
    cancelConfirm: "Cancel request",
    cancelFailed: "Could not cancel the request",
    stateRequested: "requested",
    stateCanceled: "canceled",
  },
  integrations: {
    title: "Connections",
    lead: "Store provider credentials encrypted for your organization. Saved is not verified. Checks are read-only.",
    newTitle: "New connection",
    name: "Name",
    provider: "Provider",
    secret: "Secret / API key",
    secretHint: "Masked input. Not stored in localStorage or client logs.",
    nameRequired: "Enter a name",
    secretMin: "Use at least 8 characters",
    secretMax: "Maximum 8192 characters",
    save: "Save encrypted",
    saveFailed: "Could not save the connection",
    boundsTitle: "Honest pilot limits",
    bound1: "No simulated LLM and no magic Mac access through these keys.",
    bound2: "No arbitrary MCP servers or SSH forms — listed providers only.",
    bound3: "Auto-provisioning and MDM are not implemented; the target model is a separate standard renter account.",
    bound4: "Use least-privilege keys; provider usage is billed by the provider.",
    empty: "No connections yet.",
    verify: "Verify",
    verifying: "Verifying…",
    delete: "Delete",
    deleteTitle: "Delete connection",
    deleteBody: "Removes the encrypted record in Tenqora Agent only. The key is not revoked at the provider.",
    deleteAck: "I understand I may still need to revoke the key at the provider.",
    deleteConfirm: "Delete record",
    deleteUnexpected: "Unexpected delete response from the server.",
    deleteFailed: "Could not delete",
    verifyFailed: "Verification failed",
    lastChecked: "last checked",
    rotate: "Rotate key",
    rotating: "Rotating…",
    rotateSave: "Save new key",
    rotateHint:
      "Replaces the encrypted secret and resets status to stored. Uses the current version as a conflict guard.",
    newSecret: "New secret / API key",
    rotateFailed: "Could not rotate the key",
    rotateConflict: "This connection changed elsewhere. Reloaded the latest version — try again.",
  },
  operator: {
    title: "Fleet overview",
    lead: "Operator view of software availability. No host shutdown or arbitrary shell.",
    users: "Users",
    tenants: "Organizations",
    tasks: "Tasks",
    machines: "Machines",
    events: "Recent events",
    noEvents: "No recent events.",
    maintenanceOn: "Enable maintenance",
    maintenanceOff: "Leave maintenance",
    reason: "Reason",
    reasonRequired: "Enter a reason (at least 8 characters)",
    confirmTitle: "Change maintenance",
    confirmBody: "This changes software availability only. Host shutdown and free-form commands are unavailable.",
    confirmAction: "Apply",
    actionFailed: "Could not update maintenance",
    serial: "serial",
  },
  admin: {
    title: "Platform administrator",
    lead: "The specification gives this role the park, platform settings, and operator roles. Every figure names its field in GET /api/v1/admin/cabinet. Choosing one machine leaves the other sections unchanged.",
    banner: "Platform administrator. Internal network only.",
    network: "Internal network",
    rangesNote: "These are the DUS1 networks this deployment already uses. A forwarded address counts only when the connection itself comes from the cluster proxy.",
    park: "Park",
    fleet: "Fleet",
    organization: "Organization",
    task: "Task",
    environment: "Environment",
    machine: "Machine",
    sourceCabinet: "GET /api/v1/admin/cabinet",
    sourceTenants: "counts.tenants",
    sourceTasks: "counts.tasks",
    sourceUsers: "counts.users",
    sourceMachines: "machines",
    installedMemory: "Installed memory",
    installedStorage: "Installed storage",
    fieldNull: "{field} is null in this response.",
    missingField: "No measurement. This response has no {field}.",
    notASeries: "This is one number, not a measurement series.",
    loadChart: "Load",
    memoryUsedChart: "Memory used",
    memoryFreeChart: "Memory remaining",
    fieldLoad: "machines[].load",
    fieldMemoryUsed: "machines[].memory_used_bytes",
    fieldMemoryFree: "machines[].memory_free_bytes",
    fieldDiskFree: "machines[].disk_free_bytes",
    fieldCpu: "machines[].cpu_count",
    alsoMissing: "Also absent from this machine",
    usersNote: "counts.users is the user count. It is not the organization count.",
    counts: "counts",
    audit: "Audit",
    clients: "Clients",
    macs: "Macs",
    journal: "Journal",
    requests: "Requests",
    sourceAudit: "GET /api/v1/audit",
    sourceRequests: "GET /api/v1/operator/workspace-requests?limit=50",
    journalEmpty: "This route returned no events.",
    journalNote: "This route returns events of the signed-in tenant.",
    requestsEmpty: "This route returned no requests.",
    clientsListNote: "tenant_name in this list comes from the workspace requests in this response. It is not counts.tenants.",
    noMachines: "No live Mac is enrolled.",
    reviewLead: "Review uses the existing operator queue. A review does not provision a Mac.",
    enrollTitle: "Record a Mac",
    enrollLead: "Creates a live record in maintenance and a one-time connector token. It does not assign the Mac to a customer, and the record is not rentable.",
    enrollName: "Name",
    serial: "Serial",
    enrollPurpose: "Purpose",
    purposeTest: "Dedicated test",
    purposeRental: "Rental inventory",
    enrollSubmit: "Record",
    enrollFailed: "Could not record the Mac",
    tokenOnce: "Shown once.",
    notConnectedTitle: "Not connected",
    notConnectedLead: "These actions stay visible so they are not mistaken for working controls. Nothing here calls a service.",
    notConnected: "Not connected",
    save: "Save",
    restore: "Restore",
    export: "Export",
    delete: "Delete",
    payment: "Payment",
    icloud: "iCloud",
    assignment: "Mac assignment",
    mail: "Mail",
    push: "Push",
    roles: "Operator roles",
    settings: "Platform settings",
    refusedTitle: "Internal network only",
    refusedBody: "This page is only available on the internal network.",
  },
  operatorWorkspace: {
    title: "Workspace requests",
    lead: "Review customer workspace requests. Notes you write are shown to the customer. No approval provisions a Mac.",
    bounds: "No infrastructure addresses, prices or SSH controls on this screen.",
    queueTitle: "Workspace request queue",
    queueLead: "Persisted customer requests awaiting operator review — not fulfillment.",
    openQueue: "Open request queue",
    stateFilter: "State",
    stateAll: "All states",
    empty: "No requests",
    emptyBody: "No workspace requests match this filter.",
    markReviewing: "Mark reviewing",
    decline: "Decline",
    reviewTitle: "Mark as reviewing",
    declineTitle: "Decline request",
    reviewConfirm: "Save review",
    declineConfirm: "Decline request",
    reviewNote: "Customer-visible note",
    noteRequired: "Enter a note (1–2000 characters)",
    actionState: "Next state",
    customerVisibleNote:
      "This note is customer-visible. Do not include infrastructure details, credentials or internal-only instructions.",
    conflict: "This request changed or the transition is invalid. Reloaded the queue — try again.",
    actionFailed: "Could not update the request",
  },
  status: {
    discovered: "discovered",
    verifying: "verifying",
    available: "available",
    reserved: "reserved",
    preparing: "preparing",
    leased: "leased",
    reclaiming: "reclaiming",
    sanitizing: "sanitizing",
    quarantined: "quarantined",
    maintenance: "maintenance",
    retired: "retired",
    requested: "requested",
    provisioning: "provisioning",
    active: "active",
    ending: "ending",
    ended: "ended",
    rejected: "rejected",
    failed: "failed",
    canceled: "canceled",
    queued: "queued",
    dispatched: "dispatched",
    running: "running",
    succeeded: "succeeded",
    cancel_requested: "cancel requested",
    accepted: "accepted",
    reconciling: "reconciling",
    healthy: "healthy",
    degraded: "degraded",
    stale: "stale",
    pending: "pending",
    unavailable: "unavailable",
    offline: "offline",
    unknown: "unknown",
    plan: "plan",
    diagnostics: "diagnostics",
    demo: "demo",
    live: "live",
    pilot: "pilot",
    openai: "OpenAI",
    anthropic: "Anthropic",
    xai: "xAI / Grok",
    google: "Google Gemini",
    github: "GitHub",
    gitlab: "GitLab.com",
    stored: "stored",
    verified: "verified",
    error: "error",
    not_checked: "not checked",
    interactive: "Interactive Mac workspace",
    "ios-build": "iOS build runner",
    "agent-workspace": "AI-agent workspace",
    request_only: "request only",
    "remote-desktop": "Remote desktop",
    ssh: "SSH",
    git: "Git",
    xcode: "Xcode",
    "gitlab-runner": "GitLab runner",
    "artifact-export": "Artifact export",
    "project-scoped-credentials": "Project-scoped credentials",
    "bounded-agent-runner": "Bounded agent runner",
    reviewing: "reviewing",
    declined: "declined",
    "lease.create": "create lease",
    "lease.end": "end lease",
    "task.create": "create task",
    "task.cancel": "cancel task",
    "planning-only": "planning only",
    not_started: "not started",
    reserve_exclusively: "Reserve exclusively",
    create_standard_account: "Create standard account",
    apply_profile: "Apply profile",
    attach_scoped_resources: "Attach scoped resources",
    verify_isolation: "Verify isolation",
    issue_access: "Issue access",
    activate_lease: "Activate lease",
    dedicated_mac_not_qualified: "Dedicated Mac not qualified",
    enrollment_and_recovery_not_qualified: "Enrollment and recovery not qualified",
    provisioning_adapter_not_enabled: "Provisioning adapter not enabled",
    isolation_not_qualified: "Isolation not qualified",
  },
  environmentSave: {
    bannerMark: "Preview.",
    banner: "Synthetic sample. Save, restore, export, and delete are not connected.",
    lead: "Ledger, Stage, and Manifest stay for comparison. Letter, Rule, and Poster stay as studies. Register is the environment cabinet.",
    back: "Back to your environment",
    variantLabel: "Layout",
    phaseLabel: "Sample state",
    railNote: "Preview chrome only. This does not open the rest of the cabinet.",
    kicker: "Not a live workspace",
    notConnected: "Not connected. These controls do not call the service.",
    variants: {
      ledger: {
        name: "Ledger",
        blurb: "A dated list of sample restore points, then retention and quota, then actions.",
      },
      stage: {
        name: "Stage",
        blurb: "One sample state fills the page. Facts stay beside it. History is not the subject.",
      },
      manifest: {
        name: "Manifest",
        blurb: "Included, excluded, cloud-only, and non-restorable items stay in separate groups.",
      },
      letter: {
        name: "Letter",
        blurb: "Four margin acts share a baseline with four passages: state, scope, record, and term.",
      },
      rule: {
        name: "Rule",
        blurb: "Five equal stations on one line. The sample state is the light word above it.",
      },
      index: {
        name: "Index",
        blurb: "Headed groups on one right edge. A long consequence sits under its row.",
      },
      poster: {
        name: "Poster",
        blurb: "One word, one deck, then four acts on a shared baseline.",
      },
    },
    studies: {
      earlier: "Kept",
      newer: "New studies",
      notFiled: "not connected",
      letterMark: "Margin. These words do not call the service.",
      letterDate: "24 October 2026 · UTC",
      letterMoments:
        "Two sample moments are mentioned only: 18 October 09:40 UTC and 22 October 16:05 UTC. Neither is a stored object.",
      letterScopeTitle: "Scope",
      letterRecordTitle: "Record",
      letterTermTitle: "Term",
      letterScope:
        "A save would hold local files, project data, and a tool and version manifest. Caches that can be rebuilt stay out. iCloud placeholders are cloud-only and are not claimed as copied. Keychain, Apple sessions, and a bootable image of the Mac would not be restored.",
      letterRecord:
        "Two moments are samples only: 18 October 2026, 09:40 UTC, and 22 October 2026, 16:05 UTC. Neither is a stored object. There is no verified save, and the size is unknown. The sample profile workspace-files-v1 is not a checked restore target.",
      letterTerm:
        "The policy, not a live meter, keeps a verified final archive for three calendar months in UTC. The extension price is unset. Quota, payment, and iCloud sign-in are not connected. Delete would require an explicit confirm and is not connected. A portable bootable Mac image is not promised.",
      ruleFoot:
        "Nothing on this line saves, restores, exports, deletes, assigns a Mac, takes payment, or signs in to iCloud.",
      ruleHold:
        "Samples 2026-10-18 09:40 UTC and 2026-10-22 16:05 UTC are not stored. Local files and project data would be held. Caches, iCloud placeholders, Keychain, and a boot image would not.",
      ruleClosed:
        "Save, restore, export, delete, quota, payment, iCloud, and Mac assignment are not connected.",
      indexMoment: "Sample. Not a stored object.",
      indexRetention: "Three months after a verified final archive. Price unset. Payment not connected.",
      indexIcloud: "Not signed in. Not a platform backup.",
      indexDelete: "Not connected. An explicit confirm would be required.",
      indexImage: "Bootable image",
      indexOrder: "Order",
      indexOrderValue: "Quiesce, export, verify",
      indexOrderGloss: "Sample order. Nothing is running.",
      indexNow: "Now",
      indexMoments: "Moments",
      indexScope: "Scope",
      indexTerm: "Term",
      indexClosed: "Not connected",
      indexClosedNote: "None of these call the service.",
      indexQuotaValue: "No figure",
      indexPayValue: "No checkout",
      indexSaveValue: "No release",
      indexRestoreValue: "No restore",
      indexExportValue: "No file",
      indexDeleteValue: "Would need confirm",
      indexMac: "Mac",
      indexMacValue: "Not assigned",
      indexMacLive: "Not ready",
      indexMacLiveGloss: "Live compute is reported. Restore is not connected.",
      indexMacDemo: "{n} demo",
      indexMacDemoGloss: "Demo lease records are not a restore Mac.",
      indexChecks: "Checks",
      indexChecksValue: "Pending",
      indexChecksIdle: "Not running",
      indexStateOff: "Live compute is off in this account response.",
      indexStateOn: "Live compute is reported. There is still no verified save.",
      indexArchiveRequests: "Archive requests",
      indexOpenCount: "{n} open",
      indexArchiveGloss: "A capacity request is not a save or an export.",
      indexExpiry: "Expiry",
      indexExpiryValue: "No date",
      indexExpiryGloss: "This response has no verified archive time, so no expiry is calculated.",
      indexPayFlag: "Flag only",
      indexPayFlagGloss: "Checkout is reported. This page has no payment control.",
      indexSaveReported: "Storage provisioning is reported. Save and release is still not connected.",
      indexSample: "Sample",
      indexNone: "None",
      indexNotChecked: "Not checked",
      indexIncludedValue: "Local files, project data",
      indexExcludedValue: "Rebuildable caches",
      indexCloudValue: "iCloud placeholders",
      indexLostValue: "Keychain, boot image",
      indexMonths: "3 months UTC",
      indexPrice: "Extension price",
      indexPriceValue: "Unset",
      indexPayment: "Payment",
      indexNotSignedIn: "Not signed in",
      indexNotPromised: "Not promised",
      indexState: {
        running: "No verified save",
        saving: "Nothing verified",
        archived: "Nothing released",
        restoring: "No access",
        attention: "Cleanup blocked",
      },
      posterVoid: "Not connected",
      posterFoot:
        "Quota unknown. Payment not connected. iCloud not signed in. No Mac is assigned. A bootable image is not promised.",
      posterScope:
        "Local files and project data would be in scope. Caches, iCloud placeholders, Keychain, and a boot image would not return.",
      posterSample: "Sample, not stored",
      posterTerm: "3 months UTC · price unset",
      posterClosed: "Quota, payment, iCloud, Mac",
    },
    phases: {
      running: {
        name: "Running",
        body: "Sample: work can continue. There is no verified save.",
      },
      saving: {
        name: "Saving",
        body: "Sample in progress. Nothing has been verified.",
      },
      archived: {
        name: "Archived",
        body: "Sample archived state. Nothing was released.",
      },
      restoring: {
        name: "Restoring",
        body: "Sample restore. Access is not granted.",
      },
      attention: {
        name: "Attention",
        body: "Sample: the save was not verified. Cleanup stays blocked. This is not a live failure.",
      },
    },
    lastSaveTitle: "Last verified save",
    lastSaveNone: "None. No verified archive is connected.",
    sizeTitle: "Size",
    sizeUnknown: "Unknown",
    retentionTitle: "Retention",
    retentionBody:
      "Policy, not a live meter: three calendar months in UTC after a final archive is verified. Extension price is not defined. Payment is not connected.",
    quotaTitle: "Quota",
    quotaBody: "Not connected. No quota figure is shown.",
    compatibilityTitle: "Compatibility",
    compatibilityBody: "Sample profile workspace-files-v1. Not a checked restore target.",
    icloudTitle: "iCloud",
    icloudBody:
      "Voluntary and external. Not a platform backup. Sign-in is not connected. Cloud-only files are not claimed as copied.",
    macReady: "A Mac is not ready while save, restore, or checks are unfinished.",
    progressSteps: "Sample order only: quiesce, export, verify. No percentage is measured.",
    pointsTitle: "Restore points",
    pointSample: "Sample row",
    pointUnverified: "Not a stored object",
    included: "Included",
    excluded: "Excluded",
    cloudOnly: "Cloud-only",
    nonRestorable: "Not restorable",
    includedItems: ["Local files", "Project data", "Tool and version manifest"],
    excludedItems: ["Caches that can be rebuilt"],
    cloudItems: ["iCloud placeholders"],
    nonRestorableItems: ["Keychain and Apple sessions", "A bootable whole-Mac image"],
    saveRelease: "Save and release",
    restore: "Restore on a Mac",
    exportAction: "Export",
    deleteAction: "Delete",
    deleteConsequence:
      "Delete would remove a verified archive only after an explicit confirm. That operation is not connected.",
    bootNote: "A portable bootable Mac image is not promised.",
  },
  demo: { chip: "DEMO", liveChip: "LIVE" },
};
